<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import {
  iButton,
  iButtonSizes,
  iButtonTypes,
  iButtonStyles,
  iFeaturedIcon,
  iFeaturedIconSizes,
} from '@inscribe/signal';
import StatusBadge from '@/V2/components/status/StatusBadge.vue';
import { Statuses } from '@/V2/components/status/utils';
import { mapActions, mapGetters } from 'vuex';
import DocumentCard, {
  DocumentCardSizes,
} from '@/V2/components/documents/DocumentCard.vue';
import CustomerEmptyState from '@/V2/customers/CustomerEmptyState.vue';
import { RISK_LEVEL, riskLevelToReadableLabel } from '@/constants/riskLevels';

export default {
  components: {
    iButton,
    iFeaturedIcon,
    ExpandableContainer,
    StatusBadge,
    DocumentCard,
    CustomerEmptyState,
  },
  data() {
    return {
      DOCUMENTS_LIMIT: 20,
      iButtonSizes,
      iButtonTypes,
      iButtonStyles,
      iFeaturedIconSizes,
      Statuses,
      DocumentCardSizes,
      RISK_LEVEL,
      riskLevelToReadableLabel,
    };
  },
  computed: {
    ...mapGetters([
      'someDocumentUploadingOrProcessing',
      'getCachedFirstPageOfSummaryDocumentsForCustomerUUID',
    ]),
    showEmptyState() {
      return (
        typeof this.displayedDocuments !== 'undefined' &&
        Object.keys(this.displayedDocuments ?? {}).length === 0
      );
    },
    returnToPath() {
      return this.$route.fullPath;
    },
    displayedDocuments() {
      return this.getCachedFirstPageOfSummaryDocumentsForCustomerUUID(
        this.$route.params.customer_id,
      );
    },
    shouldShowLoadingState() {
      const firstDocIsUploadingOrProcessing =
        !this.displayedDocuments?.length &&
        this.someDocumentUploadingOrProcessing;
      return (
        typeof this.displayedDocuments === 'undefined' ||
        firstDocIsUploadingOrProcessing
      );
    },
    documentsRiskGroups() {
      const riskGroups = {};
      this.displayedDocuments?.forEach((document) => {
        let riskLevel = document.risk_level ?? 'NO_RISK_RATING';
        if (!riskGroups[riskLevel]) {
          riskGroups[riskLevel] = [];
        }
        riskGroups[riskLevel].push(document);
      });
      return riskGroups;
    },
    displayedRiskLevels() {
      return [
        RISK_LEVEL.HIGH,
        RISK_LEVEL.MEDIUM,
        RISK_LEVEL.LOW,
        RISK_LEVEL.NOT_APPLICABLE,
        'NO_RISK_RATING',
      ];
    },
  },
  methods: {
    onDocumentClick(document) {
      this.$router
        .push({
          name: 'document-signals',
          params: { document_id: document.id },
          query: {
            return_to: this.returnToPath,
          },
        })
        .catch(() => {});
    },
    onViewAllClick() {
      this.$router
        .push({
          name: 'customer-documents',
          params: { customer_id: this.$route.params.customer_id },
        })
        .catch(() => {});
    },
    onUploadDocumentsClick() {
      this.$emit('upload-documents');
    },
    getLabelForRiskLevel(risk) {
      return riskLevelToReadableLabel(risk) || 'No risk rating';
    },
  },
};
</script>

<template>
  <div class="customer-fraud-documents">
    <ExpandableContainer title="Documents">
      <template #tools>
        <i-button
          v-if="!showEmptyState"
          text="Manage Documents"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.OUTLINE"
          @button-click="onViewAllClick"
          data-dd-action-name="customer-view-documents-list-manage-documents-button"
        />
      </template>
      <template #items>
        <div class="documents-list d-flex flex-column">
          <div
            v-if="shouldShowLoadingState"
            class="empty-list d-flex flex-column"
            data-testid="customer-fraud-documents-loading"
          >
            <document-card
              v-for="i in 3"
              :key="i"
              :loading="true"
              :size="DocumentCardSizes.SMALL"
            ></document-card>
          </div>

          <customer-empty-state
            v-else-if="showEmptyState"
            title="No Documents Analyzed"
            description="Upload documents to begin analysis"
            icon="medium-file"
            @button-click="onUploadDocumentsClick"
            data-testid="customer-fraud-documents-empty-state"
          />

          <template v-else>
            <div class="list-groups-container d-flex flex-column i-gap-2">
              <template v-for="risk in displayedRiskLevels" :key="risk">
                <div
                  v-if="documentsRiskGroups[risk]?.length > 0"
                  class="list-group d-flex flex-column i-radius-3xl"
                >
                  <div class="title i-text-subheading-3 i-px-9 i-py-5">
                    {{ getLabelForRiskLevel(risk) }}
                  </div>
                  <div class="documents-list items-list d-flex flex-column">
                    <document-card
                      v-for="document in documentsRiskGroups[risk]"
                      :key="document.id"
                      :document="document"
                      :size="DocumentCardSizes.SMALL"
                      :return-to="returnToPath"
                      class="document-card item-card"
                      @on-document-click="onDocumentClick"
                    ></document-card>
                  </div></div
              ></template>
            </div>
          </template>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.documents-list {
  border: 0;
  gap: 1px;

  .empty-list {
    width: 100%;
    gap: 1px;

    .expandable-item {
      width: 100%;
    }
  }

  .document-card {
    border-radius: 0;
    border: 0;
  }
}
</style>
